/**
 * Valida se o domínio de um email existe verificando registros DNS MX
 * @param email - Email a ser validado
 * @returns Promise<boolean> - true se o domínio existe, false caso contrário
 */
export const validateEmailDomain = async (email: string): Promise<boolean> => {
  try {
    // Extrai o domínio do email
    const domain = email.split('@')[1];
    
    if (!domain) {
      return false;
    }

    // Lista de domínios conhecidos e confiáveis que sempre devem passar
    const trustedDomains = [
      'gmail.com',
      'outlook.com',
      'hotmail.com',
      'yahoo.com',
      'icloud.com',
      'live.com',
      'msn.com',
      'aol.com',
      'protonmail.com',
      'zoho.com',
    ];

    // Se for um domínio confiável, retorna true imediatamente
    if (trustedDomains.includes(domain.toLowerCase())) {
      return true;
    }

    // Verifica se o domínio existe fazendo uma requisição DNS
    // Usamos uma API pública para verificar registros MX
    const response = await fetch(
      `https://dns.google/resolve?name=${domain}&type=MX`,
      {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      }
    );

    if (!response.ok) {
      // Se a API falhar, tenta uma abordagem alternativa
      return await fallbackDomainValidation(domain);
    }

    const data = await response.json();
    
    // Verifica se existem registros MX (Mail Exchange)
    // Um domínio válido para email deve ter registros MX
    return data.Status === 0 && data.Answer && data.Answer.length > 0;
  } catch (error) {
    // Em caso de erro na validação, tenta o fallback
    return await fallbackDomainValidation(email.split('@')[1]);
  }
};

/**
 * Validação alternativa usando verificação de DNS A record
 */
const fallbackDomainValidation = async (domain: string): Promise<boolean> => {
  try {
    // Verifica se existe um registro A (endereço IP) para o domínio
    const response = await fetch(
      `https://dns.google/resolve?name=${domain}&type=A`,
      {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      }
    );

    if (!response.ok) {
      // Se ambas as validações falharem, retorna false
      return false;
    }

    const data = await response.json();
    
    // Verifica se existe pelo menos um registro A
    return data.Status === 0 && data.Answer && data.Answer.length > 0;
  } catch (error) {
    // Em caso de erro, assume que o domínio não existe
    console.error('Erro ao validar domínio:', error);
    return false;
  }
};
